import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { api } from '@consts/url.const';
import { WorkflowModel } from '@models/workflow.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  constructor(private http: HttpClient) { }

  public getWorkflows(
    orgId: string,
  ): Observable<WorkflowModel.WorkflowDocument[]> {
    return this.http.get<WorkflowModel.WorkflowDocument[]>(
      api.workflows.getWorkflows(orgId),
    );
  }

  public removeWorkflow(
    id: string,
    orgId: string,
  ): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(
      api.workflows.getGraph(id, orgId),
    );
  }

  public initGraph(
    graph: WorkflowModel.InitGraphDto,
  ): Observable<WorkflowModel.GetGraphResponse> {
    const payload = {
      name: 'Untitled Workflow',
    };

    return this.http.post<WorkflowModel.GetGraphResponse>(
      api.workflows.initGraph(graph.orgId),
      payload,
    );
  }

  public getGraph(
    id: string,
    orgId: string,
  ): Observable<WorkflowModel.GetGraphResponse> {
    return this.http.get<WorkflowModel.GetGraphResponse>(
      api.workflows.getGraph(id, orgId),
    );
  }

  public updateGraph(
    graphMetadata: WorkflowModel.UpdateGraphMetadataDto,
    id: string,
    orgId: string,
  ): Observable<WorkflowModel.GetGraphResponse> {
    return this.http.put<WorkflowModel.GetGraphResponse>(
      api.workflows.updateGraph(id, orgId),
      graphMetadata,
    );
  }

  public initNode(
    orgId: string,
    graphId: string,
    node: WorkflowModel.AddNodeToGraphDto,
  ): Observable<WorkflowModel.NodeConfigDocument> {
    return this.http.post<WorkflowModel.NodeConfigDocument>(
      api.workflows.initNode(orgId, graphId),
      node,
    );
  }

  public editNode(
    orgId: string,
    graphId: string,
    id: string,
    updateNodeOptions: WorkflowModel.UpdateNodeOptionsRequestDto,
  ): Observable<WorkflowModel.NodeConfigDocument> {
    return this.http.patch<WorkflowModel.NodeConfigDocument>(
      api.workflows.editNode(orgId, graphId, id),
      updateNodeOptions,
    );
  }

  public getNode(
    orgId: string,
    graphId: string,
    id: string,
  ): Observable<WorkflowModel.NodeConfigDocument> {
    return this.http.get<WorkflowModel.NodeConfigDocument>(
      api.workflows.getNode(orgId, graphId, id),
    );
  }

  public deleteNode(
    orgId: string,
    graphId: string,
    id: string,
  ): Observable<WorkflowModel.NodeConfigDocument> {
    return this.http.delete<WorkflowModel.NodeConfigDocument>(
      api.workflows.deleteNode(orgId, graphId, id),
    );
  }

  public updateConnections(
    orgId: string,
    graphId: string,
    updateConnections: WorkflowModel.UpdateConnectionsDto,
  ): Observable<WorkflowModel.GetGraphResponse> {
    return this.http.post<WorkflowModel.GetGraphResponse>(
      api.workflows.updateConnections(orgId, graphId),
      updateConnections,
    );
  }

  public runGraph(
    orgId: string,
    graphId: string,
    runGraph: WorkflowModel.RunGraphWorkflowDto,
  ): Observable<WorkflowModel.GetGraphResponse> {
    return this.http.post<WorkflowModel.GetGraphResponse>(
      api.workflows.runGraph(orgId, graphId),
      runGraph,
    );
  }

  public publishGraph(
    orgId: string,
    graphId: string,
  ): Observable<WorkflowModel.GetGraphResponse> {
    return this.http.post<WorkflowModel.GetGraphResponse>(
      api.workflows.publishGraph(orgId, graphId),
      {},
    );
  }

  public enableGraph(
    orgId: string,
    graphId: string,
  ): Observable<WorkflowModel.GetGraphResponse> {
    return this.http.post<WorkflowModel.GetGraphResponse>(
      api.workflows.enableGraph(orgId, graphId),
      {},
    );
  }

  public disableGraph(
    orgId: string,
    graphId: string,
  ): Observable<WorkflowModel.GetGraphResponse> {
    return this.http.post<WorkflowModel.GetGraphResponse>(
      api.workflows.disableGraph(orgId, graphId),
      {},
    );
  }

  public getGraphRunList(
    orgId: string,
    graphId: string,
  ): Observable<WorkflowModel.WorkflowRunSummaryItem[]> {
    return this.http.get<WorkflowModel.WorkflowRunSummaryItem[]>(
      api.workflows.getGraphRunList(orgId, graphId),
    );
  }

  public cancelGraphRun(
    orgId: string,
    graphId: string,
    runId: string,
  ): Observable<WorkflowModel.CancelRunResponse> {
    return this.http.post<WorkflowModel.CancelRunResponse>(
      api.workflows.cancelGraphRun(orgId, graphId, runId),
      {}
    );
  }

  public getGraphRunSteps(
    orgId: string,
    graphId: string,
    runId: string,
    view?: 'summary' | 'full',
  ): Observable<WorkflowModel.GetRunStepsResponse> {
    let params = new HttpParams();

    if (view) {
      params = params.set('view', view)
    }

    return this.http.get<WorkflowModel.GetRunStepsResponse>(
      api.workflows.getGraphRunSteps(orgId, graphId, runId),
      { params }
    );
  }

  public getGraphRunStepInfo(
    orgId: string,
    graphId: string,
    runId: string,
    stepId: string,
  ): Observable<WorkflowModel.WorkflowRunStepView> {
    return this.http.get<WorkflowModel.WorkflowRunStepView>(
      api.workflows.getGraphRunStepInfo(orgId, graphId, runId, stepId),
    );
  }
}
